import { inject, Injectable } from "@angular/core";
import { first, map } from "rxjs";
import { GraphService } from "@kernel/graphql/services/graph.service";
import { IVehicleTransfer, IVehicleTransferSummary } from "../interfaces/vehicle-transfer.interface";
import { VEHICLE_TRANSFER_FORM_QUERY } from "../queries/vehicle-transfer-form.query";
import {
  IMarkVehicleTransferAsDeliveredInput,
  IMarkVehicleTransferAsPickedUpInput,
  IVehicleTransferCancelInput,
  IVehicleTransferRescheduleInput,
} from "../interfaces/vehicle-transfer-input.interface";

/**
 * API service for vehicle-transfer GraphQL operations.
 */
@Injectable()
export class VehicleTransferApiService {
  /** GraphQL service used for executing queries and mutations. */
  private readonly graphService = inject(GraphService);

  /**
   * Fetches the vehicle-transfer counters displayed by the list page summary tabs.
   */
  getVehicleTransferSummary() {
    return this.graphService
      .constructQuery<{ getVehicleTransferSummary: IVehicleTransferSummary }>(
        `
          allCount
          plannedCount
          inProgressCount
          cancelledCount
          closedCount
        `,
        "getVehicleTransferSummary"
      )
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data.getVehicleTransferSummary;
        })
      );
  }

  /**
   * Fetches a vehicle transfer by its id.
   */
  getVehicleTransfer(id: string, dataShape: string | string[] = VEHICLE_TRANSFER_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService.constructQuery<{ getVehicleTransfer: IVehicleTransfer }>(shape, "getVehicleTransfer", { id }).pipe(
      first(),
      map(data => {
        if (data.errors?.length) {
          throw data.errors[0];
        }
        return data.data.getVehicleTransfer;
      })
    );
  }

  /**
   * Reschedules a vehicle transfer.
   *
   * @param id - The id of the transfer.
   * @param input - The replacement schedule and reason.
   * @param dataShape - The shape of the response data. Defaults to the form query.
   * @returns An observable emitting the updated `IVehicleTransfer`.
   * @throws GraphQL error if the response contains errors.
   */
  rescheduleVehicleTransfer(id: string, input: IVehicleTransferRescheduleInput, dataShape: string | string[] = VEHICLE_TRANSFER_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        rescheduleVehicleTransfer: IVehicleTransfer;
      }>("rescheduleVehicleTransfer", { id: "ID!", input: "RescheduleVehicleTransferInput!" }, { id, input }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.rescheduleVehicleTransfer;
        })
      );
  }

  /**
   * Cancels a vehicle transfer. No further management actions remain available afterwards.
   *
   * @param id - The id of the transfer.
   * @param input - Cancellation reason.
   * @param dataShape - The shape of the response data. Defaults to the form query.
   * @returns An observable emitting the cancelled `IVehicleTransfer`.
   * @throws GraphQL error if the response contains errors.
   */
  cancelVehicleTransfer(id: string, input: IVehicleTransferCancelInput, dataShape: string | string[] = VEHICLE_TRANSFER_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        cancelVehicleTransfer: IVehicleTransfer;
      }>("cancelVehicleTransfer", { id: "ID!", input: "CancelVehicleTransferInput!" }, { id, input }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.cancelVehicleTransfer;
        })
      );
  }

  /**
   * Ticks or unticks one vehicle of a transfer as delivered. Nothing else changes until the delivery is confirmed.
   *
   * @param id - The id of the transfer.
   * @param vehicle - The id of the vehicle, which must belong to the transfer.
   * @param delivered - True to tick the vehicle, false to untick it.
   * @param dataShape - The shape of the response data. Defaults to the form query.
   * @returns An observable emitting the updated `IVehicleTransfer`.
   * @throws GraphQL error if the response contains errors.
   */
  toggleVehicleTransferDeliveredVehicle(id: string, vehicle: string, delivered: boolean, dataShape: string | string[] = VEHICLE_TRANSFER_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        toggleVehicleTransferDeliveredVehicle: IVehicleTransfer;
      }>(
        "toggleVehicleTransferDeliveredVehicle",
        { id: "ID!", input: "ToggleVehicleTransferDeliveredVehicleInput!" },
        { id, input: { vehicle, delivered } },
        shape
      )
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.toggleVehicleTransferDeliveredVehicle;
        })
      );
  }

  /**
   * Starts the delivery of a transfer (`isInOnboarding` becomes true). Safe to call again.
   *
   * @param id - The id of the transfer.
   * @param dataShape - The shape of the response data. Defaults to the form query.
   * @returns An observable emitting the updated `IVehicleTransfer`.
   * @throws GraphQL error if the response contains errors.
   */
  startVehicleTransferDelivery(id: string, dataShape: string | string[] = VEHICLE_TRANSFER_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        startVehicleTransferDelivery: IVehicleTransfer;
      }>("startVehicleTransferDelivery", { id: "ID!" }, { id }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.startVehicleTransferDelivery;
        })
      );
  }

  /**
   * Confirms the delivery: commits the ticked vehicles' onboardings, makes them available, disables the unticked ones
   * and closes the transfer.
   *
   * @param id - The id of the transfer.
   * @param input - The new files and the signed delivery transport documents.
   * @param dataShape - The shape of the response data. Defaults to the form query.
   * @returns An observable emitting the closed `IVehicleTransfer`.
   * @throws GraphQL error if the response contains errors.
   */
  markVehicleTransferAsDelivered(
    id: string,
    input: IMarkVehicleTransferAsDeliveredInput,
    dataShape: string | string[] = VEHICLE_TRANSFER_FORM_QUERY
  ) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        markVehicleTransferAsDelivered: IVehicleTransfer;
      }>("markVehicleTransferAsDelivered", { id: "ID!", input: "MarkVehicleTransferAsDeliveredInput!" }, { id, input }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.markVehicleTransferAsDelivered;
        })
      );
  }

  /**
   * Starts the pick-up of an Internal transfer (`isInOnboarding` becomes true). Safe to call again. A Contract End is
   * picked up without starting (`PICK_UP_ONBOARDING_NOT_ALLOWED`).
   *
   * @param id - The id of the transfer.
   * @param dataShape - The shape of the response data. Defaults to the form query.
   * @returns An observable emitting the updated `IVehicleTransfer`.
   * @throws GraphQL error if the response contains errors.
   */
  startVehicleTransferPickUp(id: string, dataShape: string | string[] = VEHICLE_TRANSFER_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        startVehicleTransferPickUp: IVehicleTransfer;
      }>("startVehicleTransferPickUp", { id: "ID!" }, { id }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.startVehicleTransferPickUp;
        })
      );
  }

  /**
   * Confirms the pick-up: picks up the vehicles (Internal: the onboarded ones, whose onboardings are committed, In
   * Progress; Contract End: all of them, Closed); the backend disables the others and deletes their open onboardings.
   *
   * @param id - The id of the transfer.
   * @param input - The signed pick-up transport documents, the note, and (Internal) the location and the date.
   * @param dataShape - The shape of the response data. Defaults to the form query.
   * @returns An observable emitting the updated `IVehicleTransfer`.
   * @throws GraphQL error if the response contains errors.
   */
  markVehicleTransferAsPickedUp(id: string, input: IMarkVehicleTransferAsPickedUpInput, dataShape: string | string[] = VEHICLE_TRANSFER_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        markVehicleTransferAsPickedUp: IVehicleTransfer;
      }>("markVehicleTransferAsPickedUp", { id: "ID!", input: "MarkVehicleTransferAsPickedUpInput!" }, { id, input }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.markVehicleTransferAsPickedUp;
        })
      );
  }
}
